import { createContext, useContext, useState, useCallback, useMemo } from "react";

interface PlayableTrack {
  youtubeId: string;
  trackTitle: string;
}

interface PlaybackSource {
  title: string;
  url: string;
}

interface VideoPlayerState {
  currentIndex: number;
  playlist: PlayableTrack[];
  artistName: string | null;
  source: PlaybackSource | null;
}

interface PlayOptions {
  playlist: PlayableTrack[];
  startIndex: number;
  artistName: string | null;
  source?: PlaybackSource | null;
}

interface VideoPlayerContextValue {
  state: VideoPlayerState | null;
  play: (options: PlayOptions) => void;
  next: () => void;
  previous: () => void;
  close: () => void;
  currentTrack: PlayableTrack | null;
  hasPrevious: boolean;
  hasNext: boolean;
}

const VideoPlayerContext = createContext<VideoPlayerContextValue>({
  state: null,
  play: () => {},
  next: () => {},
  previous: () => {},
  close: () => {},
  currentTrack: null,
  hasPrevious: false,
  hasNext: false,
});

export function VideoPlayerProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<VideoPlayerState | null>(null);

  const play = useCallback(({ playlist, startIndex, artistName, source }: PlayOptions) => {
    setState({ playlist, currentIndex: startIndex, artistName, source: source ?? null });
  }, []);

  const next = useCallback(() => {
    setState((prev) => {
      if (!prev || prev.currentIndex >= prev.playlist.length - 1) return prev;
      return { ...prev, currentIndex: prev.currentIndex + 1 };
    });
  }, []);

  const previous = useCallback(() => {
    setState((prev) => {
      if (!prev || prev.currentIndex <= 0) return prev;
      return { ...prev, currentIndex: prev.currentIndex - 1 };
    });
  }, []);

  const close = useCallback(() => setState(null), []);

  const currentTrack = state?.playlist[state.currentIndex] ?? null;
  const hasPrevious = (state?.currentIndex ?? 0) > 0;
  const hasNext = state ? state.currentIndex < state.playlist.length - 1 : false;

  const value = useMemo<VideoPlayerContextValue>(
    () => ({ state, play, next, previous, close, currentTrack, hasPrevious, hasNext }),
    [state, play, next, previous, close, currentTrack, hasPrevious, hasNext],
  );

  return <VideoPlayerContext.Provider value={value}>{children}</VideoPlayerContext.Provider>;
}

export function useVideoPlayer() {
  return useContext(VideoPlayerContext);
}
